/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import Highcharts, {
  Axis, Chart, ChartSelectionContextObject, Point, PointerEventObject,
} from 'highcharts';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { useConditionalEffect, useDelta } from 'react-delta';
import classNames from 'classnames';

import { logSearchMatchCountTitleState } from 'api/apolloClient.state';
import {
  completeFilterState,
  eventParamsState,
  queryTimeRangeState,
  teamEmailsState,
} from 'components/common/Managers/RouteStateManager.state';
import {  generateLogEntriesRequest } from 'hooks/useLRQ/useLRQ';
import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { convertTimestampToMilliseconds, getDateRangeText, getDurationText } from 'common/util/time';
import { getLinkForRoute } from 'common/util/location';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { HoverData, TimeRange, UrlParams } from 'common/types';
import { adjustBucketAtMinMax } from 'common/util/graph';
import { SliceInfo, SortOrder } from 'common/graphqlTypes';
import { setMark } from 'common/PerformanceLogger';

import TimelineConfig from 'components/search/SearchLogsPage/TimelineConfig';
import TimelineLegend from 'components/common/Legends/TimelineLegend';
import { searchPageRemainingWidthSelector } from 'components/common/Managers/LayoutStateManager';
import {
  logResultsScrollTimeMarker,
  visibleCenterIndexState,
} from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import { highchartsStylesState, highchartsThemeColorsState } from 'styles/stylesProvider';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';
import {
  anchorLogRequestsState,
  eventSortOrderState,
  isAtEndTimeState,
  isAtStartTimeState,
  isScrollReadyState,
  paginatedLogRequestsState,
} from 'components/search/SearchLogsPage/SearchStateManager.state';

import 'components/search/SearchLogsPage/LogResultsTimeline.scss';



// // Disabled by default to avoid console pollution
// // Allow `why-did-you-render` to hook into this component and listen for redundant re-renders during development
// (HighchartsReact as (typeof HighchartsReact & WhyDidYouRenderComponentMember)).whyDidYouRender = {
//   customName: '-> HighchartsReact from highcharts-react-official',
// };

// add missing fields to Point
type HighchartsPoint = Point & {
  clientX: number;
  clientY: number;
  pointWidth: number;
};

// add missing field to Axis
type HighchartsAxis = Axis & {
  dataMax: number;
};

export type LogResultsTimelineProps = {
  columnSeriesData:  Array<[number, number]>;
  lineSeriesData:    Array<[number, number]>;
  columnSliceInfo:   SliceInfo;
  matchCount:        number;
  noTimelineLegend?: boolean;
};

type UseHoverHandlerReturn = {
  eventsMouseOverHandler: Highcharts.PointMouseOverCallbackFunction;
  hoverData: HoverData | null;
  resetHoverData: () => void;
};

const TIMELINE_PLOT_LINE_ID = 'timeline-plot-line';

function useHoverHandler(columnSliceInfo: SliceInfo, timezone: string | null): UseHoverHandlerReturn {
  // Hover coordinates within the timeline
  const [x, setX] = useState<number | null>(null);
  const [y, setY] = useState<number | null>(null);
  const eventsMouseOverHandler: Highcharts.PointMouseOverCallbackFunction = useCallback((event: Event) => {
    const { x: pointX = null, y: pointY = null } = (event.target as unknown as HighchartsPoint) || {};
    // Adding settimeout function here to resolve weird undefined series js error in highchart on mousemove event
    setTimeout(() => {
      setX(pointX);
      setY(pointY);
    });
  }, [setX, setY]);

  // Hover data consumed by the legend
  const [ hoverData, setHoverData ] = useState<HoverData | null>(null);
  const resetHoverData = useCallback(() => {
    setHoverData(null);
    setX(null);
    setY(null);
  }, [setHoverData]);

  // Calculate HoverData only when the result may differ
  useEffect(() => {
    if (x == null || y == null) { return; }

    const bucketSizeMs = columnSliceInfo.defaultSize;
    const startTimeCurrent = x - bucketSizeMs / 2;
    const endTimeCurrent = startTimeCurrent + bucketSizeMs;
    const dateRangeText = getDateRangeText(startTimeCurrent, endTimeCurrent, timezone, true, true);
    const numEvents = y.toLocaleString(navigator.language, { maximumFractionDigits: 0 });

    setHoverData({
      time: dateRangeText,
      value: numEvents,
    });
  }, [x, y, columnSliceInfo]);

  return { eventsMouseOverHandler, hoverData, resetHoverData };
}

/**
 * Timeline graph component in search page, including the legend.
 *
 * @prop noTimelineLegend    if true, do not show a legend above the timeline
 */
function LogResultsTimeline({
  columnSeriesData,
  lineSeriesData,
  columnSliceInfo,
  matchCount,
  noTimelineLegend,
}: LogResultsTimelineProps): JSX.Element {

  useLayoutEffect(() => {
    setMark('timelineRendered', { matchCount }, { debounce: 100 });
  });

  const chartRef = useRef<Chart>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timelineRef = useRef<HTMLDivElement>(null);
  const isSelectionEvent = useRef<boolean>(false); // to omit handling 'click' event after 'selection' happens

  const [ subtitle, setSubtitle ] = useState('');
  const timezone = useRecoilValue(timezoneState);
  const { eventsMouseOverHandler, hoverData, resetHoverData } = useHoverHandler(columnSliceInfo, timezone);
  const eventParams = useRecoilValue(eventParamsState);
  const completeFilter = useRecoilValue(completeFilterState);
  const remainingWidth = useRecoilValue(searchPageRemainingWidthSelector);
  const teamEmails = useRecoilValue(teamEmailsState);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const setSearchParams = useSetSearchParams();
  const { startTime, endTime, startTimeNs, endTimeNs, requestTime } = queryTimeRange;
  const sortOrder = useRecoilValue(eventSortOrderState);
  const isAtStartTime = useRecoilValue(isAtStartTimeState);
  const isAtEndTime = useRecoilValue(isAtEndTimeState);

  const highchartsStyles = useRecoilValue(highchartsStylesState);
  const highchartsThemeColors = useRecoilValue(highchartsThemeColorsState);

  /**
   * Draw a plot line on the chart.
   * @param {number} xValue   A value in terms of axis units, eg. 15434234234.
   */
  const drawMarkerOnTimeline = useCallback((xValue: number): void => {
    if (chartRef.current) {

      chartRef.current.xAxis[0].removePlotLine(TIMELINE_PLOT_LINE_ID);

      chartRef.current.xAxis[0].addPlotLine({
        value: xValue,
        color: highchartsThemeColors.plotLineMarkerColor,
        width: 2,
        zIndex: 4, // greater than crosshair z-index 3
        id: TIMELINE_PLOT_LINE_ID,
      });
    }
  }, [highchartsStyles]);

  const runSearch = useCallback((start: string, end: string): void => {
    const timeRange: TimeRange = {
      startTime: start,
      endTime:   end,
    };
    const urlParams: UrlParams = { ...timeRange } as UrlParams;
    setSearchParams(urlParams);
  }, [setSearchParams]);

  /*
    Timeline position marker for logResults scroll feature
   */
  const [ timeMarker, setTimeMarker ] = useRecoilState(logResultsScrollTimeMarker);
  useEffect(() => {
    const xAxis = chartRef.current?.xAxis?.[0] as HighchartsAxis;
    const xAxisExtremes = xAxis?.getExtremes();

    // Handle min/max scroll state
    if (isAtStartTime && xAxisExtremes) {
      drawMarkerOnTimeline(xAxisExtremes.min);
      return;
    }
    if (isAtEndTime && xAxisExtremes) {
      drawMarkerOnTimeline(xAxisExtremes.max);
      return;
    }

    drawMarkerOnTimeline(timeMarker);
  }, [drawMarkerOnTimeline, isAtStartTime, isAtEndTime, timeMarker]);

  const resetIsScrollReady = useResetRecoilState(isScrollReadyState);
  const resetVisibleCenterIndex = useResetRecoilState(visibleCenterIndexState);
  const setAnchorLogRequests = useSetRecoilState(anchorLogRequestsState);
  const setPaginatedLogRequests = useSetRecoilState(paginatedLogRequestsState);
  const timelineLegendTitle = useRecoilValue(logSearchMatchCountTitleState);

  const [ chartOptions, setChartOptions ] = useState<Highcharts.Options>(merge(TimelineConfig, {
    chart: {
      events: {
        // Capture the mouse selection event
        selection: (e: ChartSelectionContextObject): boolean | undefined => {
          if (e?.xAxis) {
            const selectionAxis = e.xAxis[0];
            const min = Math.floor(selectionAxis.min).toString();
            const max = Math.floor(selectionAxis.max).toString();

            isSelectionEvent.current = true; // to inform following click event handler that the root cause is 'selection'
            runSearch(min, max);
          }
          return false;
        },
      },
    },
    plotOptions: {
      column: {
        point: {
          events: {
            mouseOver: eventsMouseOverHandler,
          },
        },
        states: {
          hover: {
            color: highchartsThemeColors.columnHoverColor,
          },
        },
      },
    },
  }, highchartsStyles));

  /**
   * Recalculate bar width and reflow the chart when the available width reserved for this component changes. For
   * example, the width of the viewport changes or the user expands or collapses the sidebar. This effect does not run
   * if the timeline chart isn't rendered, or on the initial render.
   */
  const remainingWidthDelta = useDelta(remainingWidth);

  useConditionalEffect(() => {
    setChartOptions(merge(cloneDeep({
      series: [
        {
          type:   K.SERIES_TYPES.COLUMN,
          pointWidth: Math.floor(remainingWidth / (K.DEFAULT_COLUMN_SPACING * columnSeriesData.length)),
        },
        {
          type:   K.SERIES_TYPES.LINE,
        },
      ],
    }), highchartsStyles));
    chartRef?.current?.reflow();
  },
  !!timelineRef?.current
  && !!remainingWidthDelta
  && remainingWidthDelta.prev !== undefined);

  /**
   * Rebuild the chart when the underlying data is updated.
   */
  useEffect(() => {
    // copy the props to avoid side effect
    const columnSeries = cloneDeep(columnSeriesData);
    const lineSeries = cloneDeep(lineSeriesData);
    const bucketSizeMs = columnSliceInfo.defaultSize;

    // adjust min and max x values based on slice; otherwise, the time range may be off a bit
    const firstSeries = columnSeries[0];
    const lastSeries = columnSeries[columnSeries.length - 1];
    const [ min, max ] = adjustBucketAtMinMax(firstSeries[0], lastSeries[0], columnSliceInfo);
    firstSeries[0] = min;
    lastSeries[0] = max;

    // scale up the line data
    lineSeries.forEach((series: Array<number>) => {
      series[1] *= (bucketSizeMs / 1000);
    });

    setChartOptions(merge(cloneDeep({
      series: [
        {
          type:   K.SERIES_TYPES.COLUMN,
          zIndex: 0,
          data:   columnSeries,
          pointWidth: Math.floor(remainingWidth / (K.DEFAULT_COLUMN_SPACING * columnSeries.length)),
        },
        {
          type:   K.SERIES_TYPES.LINE,
          zIndex: 1,
          data:   lineSeries,
          dashStyle: 'ShortDot',
        },
      ],
      xAxis: {
        type: 'datetime',
        min: Math.min(convertTimestampToMilliseconds(startTime), min),
        max: convertTimestampToMilliseconds(endTime),
      },
    }), highchartsStyles));

    setSubtitle(`(${getDurationText(bucketSizeMs)} / bar)`);
  }, [columnSeriesData, lineSeriesData, columnSliceInfo, startTime, endTime, highchartsStyles, timezone]);

  const generateCenteredSearch = useCallback((value: number): void => {
    const centerTime = Math.floor(value);
    if (Number(startTime) > centerTime || centerTime > Number(endTime)) {
      return;
    }
    const descendingRequest = generateLogEntriesRequest({
      ascending: false,
      startTime: startTimeNs,
      endTime: String(centerTime),
      filter: completeFilter,
      offset: '',
      requestTime,
      numResults: K.NUM_INITIAL_LOG_ENTRIES,
      teamEmails,
    });
    const ascendingRequest = generateLogEntriesRequest({
      ascending: true,
      startTime: String(centerTime),
      endTime: endTimeNs,
      filter: completeFilter,
      offset: '',
      requestTime,
      numResults: K.NUM_INITIAL_LOG_ENTRIES,
      teamEmails,
    });
    resetIsScrollReady();
    resetVisibleCenterIndex();
    // For centered searches, the ascending request may need to go before or after the descending request, depending on sort order
    setAnchorLogRequests(
      sortOrder === SortOrder.MOST_RECENT_AT_TOP
        ? [ascendingRequest, descendingRequest]
        : [descendingRequest, ascendingRequest],
    );
    setPaginatedLogRequests(K.EMPTY_ARRAY);
    setTimeMarker(Math.floor(value));
  }, [completeFilter, endTime, requestTime, resetIsScrollReady, resetVisibleCenterIndex, setAnchorLogRequests, setPaginatedLogRequests, setTimeMarker, startTime, teamEmails]);

  const handleChartClick = useCallback((event: SyntheticEvent): void => {
    if (chartRef.current) {
      if (isSelectionEvent.current) { // skip handling event if the 'selection' caused it
        isSelectionEvent.current = false;
        return;
      }

      const nativeEvent = event.nativeEvent as PointerEventObject;
      const values = chartRef.current.pointer.getCoordinates(nativeEvent);
      generateCenteredSearch(values.xAxis[0].value);
    }
  }, [generateCenteredSearch]);

  /**
   * A callback provided by HighchartsReact to store the chart object
   */
  const setupChartInfo = useCallback((chartFactory: Chart): void => {
    chartRef.current = chartFactory;
  }, []);

  const graphPageLink = getLinkForRoute(K.ROUTE.GRAPH, {
    ...eventParams,
    graphStyle: K.CHART_TYPES.LINE,
    barWidth: K.AUTO_BAR_WIDTH,
  });

  const containerProps = useMemo(() => ({
    onClick: handleChartClick,
    onMouseLeave: resetHoverData,
    style: {
      width: '100%',
    },
  }), [handleChartClick, resetHoverData]);


  const legend = noTimelineLegend ? null : (
    <TimelineLegend
      timeRange={{
        startTime: Number(startTime) || 0,
        endTime: Number(endTime) || 0,
      }}
      timeRangeText={hoverData?.time}
      value={hoverData?.value}
      title={timelineLegendTitle}
      subtitle={subtitle.toLowerCase()}
      buttonText="Expand Graph"
      buttonLink={graphPageLink}
      buttonTestId={ELEMENT.timelineLegendShowGraph}
    />
  );

  const className = classNames('LogResultsTimeline', {
    'LogResultsTimeline--Legend': !noTimelineLegend,
    'LogResultsTimeline--NoTimelineLegend': noTimelineLegend,
  });

  return (
    <div className={className} ref={timelineRef}>
      {legend}
      <ScalyrHighcharts
        options={chartOptions}
        highcharts={Highcharts}
        callback={setupChartInfo}
        containerProps={containerProps}
      />
    </div>
  );
}

// // Disabled by default to avoid console pollution
// LogResultsTimeline.whyDidYouRender = {
//   customName: '-> LogResultsTimeline',
// };

export default LogResultsTimeline;
